import { useEffect, useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { caseStudies } from "../data/projects";
import { LineReveal, Reveal, EASE } from "../components/Motion";
import { Collage } from "../components/Collage";
import { Contact } from "../components/Contact";
import { CountUp } from "../components/CountUp";
import { lenisRef } from "../lib/smooth";
import { useLightbox } from "../components/Lightbox";

const HeroImage = ({ src, pos = "object-center" }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  const { open } = useLightbox();
  return (
    <motion.div
      ref={ref}
      className="relative mx-5 h-[60vw] cursor-zoom-in overflow-hidden md:mx-10 md:h-[48vw]"
      onClick={() => open([src])}
      initial={{ clipPath: "inset(18% 12% 18% 12%)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
      transition={{ duration: 1.5, ease: EASE, delay: 0.5 }}
      data-testid="case-hero-image"
    >
      <motion.img src={src} alt="" style={{ y }} className={`absolute inset-0 h-[120%] w-full -translate-y-[10%] object-cover ${pos}`} />
    </motion.div>
  );
};

const Chapter = ({ c }) => (
  <section className="px-5 py-16 md:px-10 md:py-28" data-testid={`chapter-${c.no}`}>
    <div className="mb-10 grid gap-6 md:mb-16 md:grid-cols-12">
      <p className="font-serif text-3xl italic text-ink md:col-span-2 md:text-4xl">{c.no}.</p>
      <LineReveal as="h2" inView lines={[c.title]} className="font-display text-4xl font-semibold leading-[0.95] tracking-[-0.045em] text-ink md:col-span-5 md:text-6xl" />
      <Reveal className="md:col-span-5" delay={0.15}>
        <p className="text-base leading-relaxed text-muted md:text-lg">{c.body}</p>
      </Reveal>
    </div>
    <Collage rows={c.rows} />
  </section>
);

const Story = ({ s }) => (
  <section className="px-5 py-16 md:px-10 md:py-28" data-testid="case-study-story">
    <div className="grid gap-10 md:grid-cols-12">
      <div className="md:col-span-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">What I did</p>
        {s.stats && <div className="mt-8 space-y-8">
          {s.stats.map((x, i) => (
            <Reveal key={x.value} delay={i * 0.1}>
              <div className="border-l-2 border-accent pl-5" data-testid={`case-study-stat-${i}`}>
                <p className="font-display text-4xl font-semibold leading-none tracking-[-0.05em] text-ink md:text-6xl"><CountUp value={x.value} /></p>
                <p className="mt-2 font-serif text-xl italic text-muted md:text-2xl">{x.label}</p>
              </div>
            </Reveal>
          ))}
        </div>}
      </div>
      <div className="space-y-6 md:col-span-6 md:col-start-7">
        {s.paragraphs.map((p, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <p className={`leading-relaxed ${i === 0 ? "text-xl text-ink md:text-2xl" : "text-base text-muted md:text-lg"}`}>{p}</p>
          </Reveal>
        ))}
        {s.link && (
          <a href={s.link.href} target="_blank" rel="noreferrer" className="pill-btn" data-testid="case-study-site-link">
            <span>{s.link.label}</span><span aria-hidden>↗</span>
          </a>
        )}
        {s.partners && (
          <Reveal>
            <div className="pt-4" data-testid="case-study-partners">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">Partners</p>
              <div className="mt-4 grid grid-cols-2 gap-px bg-[#2a3344] sm:grid-cols-4">
                {s.partners.map((x) => (
                  <div key={x.name} className="flex h-24 items-center justify-center bg-[#121923] px-4 transition-transform duration-500 hover:-translate-y-1" data-testid={`partner-logo-${x.name.toLowerCase().replace(/\s+/g, "-")}`}>
                    <img src={x.src} alt={x.name} className="max-h-12 w-auto object-contain" />
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const i = caseStudies.findIndex((c) => c.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
    lenisRef.current?.scrollTo(0, { immediate: true });
  }, [slug]);
  if (i < 0) return <Navigate to="/" replace />;
  const p = caseStudies[i];
  const next = caseStudies[(i + 1) % caseStudies.length];
  return (
    <main key={slug} data-testid={`case-study-page-${p.slug}`}>
      <section className="px-5 pb-12 pt-28 md:px-10 md:pb-16 md:pt-36">
        <Link to="/#work" className="nav-link font-mono text-[11px] uppercase tracking-[0.18em] text-ink" data-testid="case-study-back-button">← All work</Link>
        <div className="mt-10 grid gap-8 md:grid-cols-12">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted md:col-span-2">Case {p.index}</p>
          <div className="md:col-span-10">
            <LineReveal
              testid="case-study-title"
              className="font-display text-[14vw] font-semibold leading-[0.88] tracking-[-0.06em] text-ink md:text-[8.5vw]"
              lines={p.title.split(" ").length > 2 ? [p.title.split(" ").slice(0, 1).join(" "), p.title.split(" ").slice(1).join(" ")] : [p.title]}
            />
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.6 }} className="mt-8 max-w-2xl text-lg leading-relaxed text-muted md:text-xl">
              {p.summary}
            </motion.p>
          </div>
        </div>
      </section>
      <HeroImage src={p.hero} pos={p.heroPos} />
      <section className="mx-5 mt-10 grid grid-cols-2 gap-6 border-y border-ink py-6 md:mx-10 md:grid-cols-4" data-testid="case-study-meta">
        {[["Client", p.title], ["Year", p.year], ["Focus", p.kicker], ["Disciplines", p.disciplines.join(", ")]].map(([k, v]) => (
          <div key={k}>
            <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted">{k}</p>
            <p className="mt-2 text-sm text-ink md:text-base">{v}</p>
          </div>
        ))}
      </section>
      {p.story && <Story s={p.story} />}
      {p.chapters.map((c) => <Chapter key={c.no} c={c} />)}
      <Link to={`/work/${next.slug}`} className="next-link group block border-t border-ink px-5 py-20 md:px-10 md:py-32" data-testid="case-study-next-link">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted">Next case study</p>
        <p className="mt-6 font-display text-[12vw] font-semibold leading-[0.9] tracking-[-0.06em] text-ink transition-colors duration-500 group-hover:text-accent md:text-[7vw]">
          {next.title} <span className="inline-block transition-transform duration-500 group-hover:translate-x-4">→</span>
        </p>
      </Link>
      <Contact />
    </main>
  );
}
